import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { MATERIAL_IMPORTS } from '../../../material/material-imports';
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';
import { SpinnerComponent } from '../../../shared/spinner/spinner.component';
import { ProblemAutoCompleteComponent } from '../../problem/problem-auto-complete/problem-auto-complete.component';
import { Location } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormGroup, Validators } from '@angular/forms';

import {
  ExerciseRequest,
  ExerciseModel,
  ProblemModel,
  SubjectModel,
} from '@generativ/much-smarter-models';

import { Observable, Subject } from 'rxjs';
import { AlertService } from '../../../core/alert/alert.service';
import { ParamsService } from '../../../shared/params.service';
import { ProblemService } from '../../../shared/problem.service';
import { FormService } from '../../../shared/form.service';
import { ExerciseService } from '../exercise.service';
import { SubjectService } from '../../../shared/subject.service';
import { CourseService } from '../../course/course.service';

@Component({
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, ...MATERIAL_IMPORTS, CapitalizePipe, SpinnerComponent, ProblemAutoCompleteComponent],
  selector: 'app-exercise',
  templateUrl: './exercise.component.html',
  styleUrls: ['./exercise.component.scss']
})
export class ExerciseComponent implements OnInit {

  exerciseId: number;
  lessonId: number;
  courseId: number;
  courseSubjects: string;
  exerciseForm: FormGroup;
  exercise: ExerciseModel;
  pageType: string;
  subjects: SubjectModel[];
  offset = 0;
  limit = 20;
  totalProblems = 0;
  searchQuery = null;

  ready = false;

  searchedProblems: Observable<ProblemModel[]>;
  problemsNotRelated: ProblemModel[] = [];
  problems: ProblemModel[] = [];
  errorMessage: string;
  isSubmitting = false;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private formService: FormService,
              private exerciseService: ExerciseService,
              private subjectService: SubjectService,
              private alertService: AlertService,
              private location: Location,
              private problemService: ProblemService,
              private paramsService: ParamsService,
              private courseService: CourseService) {
    this.subjectService.getSubjects().subscribe(
      subjects => {
        this.subjects = subjects;
      }
    );
    this.exerciseForm = this.formService.createForm(this.formOptions);
  }

  ngOnInit() {
    this.lessonId = +this.paramsService.getParamById('lessonId', this.route.snapshot);
    this.courseId = +this.paramsService.getParamById('courseId', this.route.snapshot);
    this.route.data.subscribe((data) => {
      this.pageType = data.type;
      this.courseService.getCourse(this.courseId).subscribe(
        courseModel => {
          this.courseSubjects = courseModel.subject;
          this.subjects = this.subjects.filter((elem, id) => {
            if (this.courseSubjects.split(',').find(elem => parseInt(elem) - 1 === id)) {
              return true;
            }
          });
        }
      );
      if (this.pageType === 'edit') {
        this.route.params.subscribe((params: Params) => {
          this.exerciseId = +params['exerciseId'];
          this.exerciseService.getExercise(this.exerciseId).subscribe(
            exerciseModel => {
              this.exercise = exerciseModel;
              // Map values from model to form values
              this.setFormValue(exerciseModel);

              if (! exerciseModel.showOpenAnswerEnabled) {
                this.exerciseForm.get('openAnswerPrompt').disable();
              }
              // Get the first 100 problems sort by title
              this.getProblems(this.offset, this.limit);
            }
          );
        });
      } else {
        this.exerciseForm.get('openAnswerPrompt').disable();
        this.ready = true;
      }
    });
  }

  submitForm() {
    if (this.formService.validateFormAndUpdateErrors(this.exerciseForm)) {
      let exerciseObservable: Observable<ExerciseModel>;
      let message;
      this.errorMessage = null;

      if (this.isSubmitting) {
        return;
      }

      if (!this.exerciseForm.valid) {
        this.errorMessage = 'Errors on form. See fields below.';
        return;
      }

      this.isSubmitting = true;

      if (this.pageType === 'create') {
        const exercise = new ExerciseRequest.Create();
        this.parseToModel(exercise);
        exercise.lessonId = this.lessonId;
        message = 'Exercise created';
        exerciseObservable = this.exerciseService.createExercise(exercise);
      } else {
        const exercise = new ExerciseRequest.Update();
        this.parseToModel(exercise);
        exercise.lessonId = this.lessonId;
        exercise.problemIds = this.exercise.problems.map(problem => problem.id);
        message = 'Exercise updated';
        exerciseObservable = this.exerciseService.updateExercise(this.exerciseId, exercise);
      }

      exerciseObservable.subscribe(
        exerciseModel => {
          this.alertService.success(message);
          if (this.pageType === 'create') {
            this.router.navigate([`../${exerciseModel.id}/manage`], {relativeTo: this.route});
          } else {
            this.location.back();
          }
        },
        err => {
          this.isSubmitting = false;
          this.alertService.error(err);
        }
      );
    }
  }

  get buttonName(): string {
    return this.pageType === 'edit' ? 'SAVE' : this.pageType.toUpperCase();
  }

  /**
   * Get Problems related with subjects.
   */
  onSelectSubject() {
    this.problemsNotRelated = this.getProblemsNotIncludedInExercise(this.exercise);
    this.getProblems(this.offset, this.limit);
  }

  /**
   * Returns problems not included in exercise.problems
   * @param exercise
   * @param problems
   * @returns {ProblemModel[]}
   */
  getProblemsNotIncludedInExercise(exercise: ExerciseModel): ProblemModel[] {
    const problemOfExerciseIds: number[] = exercise.problems.length > 0 ? exercise.problems.map(item => item.id) : [];
    return this.problems.filter(problem => !problemOfExerciseIds.includes(problem.id));
  }

  /**
   * Method used when one problem need to be
   * @param problem
   * @param add
   */
  addOrRemoveProblem(problem: ProblemModel, add: boolean) {
    if (problem) {
      if (add) {
        this.exercise.problems = this.exercise.problems.concat(problem);
        this.removeProblemOfArray(this.problemsNotRelated, problem);
      } else {
        this.removeProblemOfArray(this.exercise.problems, problem);
        this.problemsNotRelated.push(problem);
      }
      const exerciseUpdate = new ExerciseRequest.Update();
      exerciseUpdate.problemIds = this.exercise.problems.map(p => p.id);
    }
  }

  /**
   * Remove problem from array of ProblemModel[]
   * @param problems
   * @param problem
   */
  removeProblemOfArray(problems: ProblemModel[], problem: ProblemModel) {
    const index = problems.findIndex(item => item.id === problem.id);
    problems.splice(index, 1);
  }

  /**
   * Navigate to create Problem.
   */
  onCreateProblemForExercise() {
    this.router.navigate(['../problems/create'], {relativeTo: this.route});
  }


  /**
   * Load more problems with the selected search query
   */
  loadMoreProblems() {
    this.offset = this.offset + this.limit;
    this.getProblems(this.offset, this.limit);
  }

  /**
   * Hits api when search query changes
   * @param searchQuery
   */
  handleQueryChange(searchQuery: string) {
    this.searchQuery = searchQuery;
    this.getProblems(this.offset, this.limit);
  }

  toggleShowOpenAnswer(){
    const value = this.exerciseForm.get('showOpenAnswerEnabled').value;
    if (!value) {
      this.exerciseForm.get('openAnswerPrompt').enable();
    } else {
      this.exerciseForm.get('openAnswerPrompt').disable();
    }
  }

  private getProblems(offset: number, limit: number) {
    // Get the first 100 problems sort by title
    this.problemService.getProblemsWithPagination(
      offset,
      limit,
      'title',
      this.searchQuery,
      this.exerciseForm.get('subject').value).subscribe(
      problemsPaginator => {
        this.problems = problemsPaginator.problems;
        this.ready = true;
        // Once we have the first 100 problems filter the problems that currently are in the
        this.problemsNotRelated = this.getProblemsNotIncludedInExercise(this.exercise);
        this.totalProblems = problemsPaginator.totalCount;
      },
      err => {
        this.alertService.error(err);
        this.ready = true;
      }
    );
  }

  /**
   * Returns true if there're more problems to load
   */
  hasMore(): boolean {
    return this.totalProblems >= this.offset + this.limit;
  }

  private parseToModel(exerciseModel: ExerciseRequest.Create | ExerciseRequest.Update): (ExerciseRequest.Create | ExerciseRequest.Update) {
    exerciseModel.parse(this.exerciseForm.value);
    exerciseModel.showOpenAnswerEnabled = this.exerciseForm.get('showOpenAnswerEnabled').value ? 1: 0;

    return exerciseModel;
  }

  private setFormValue(exerciseModel: ExerciseModel) {
    this.exerciseForm.patchValue(exerciseModel);
    this.exerciseForm.get('showOpenAnswerEnabled').setValue(!!exerciseModel.showOpenAnswerEnabled);
  }

  // TODO: Confirm that the OpenAnswer fields & the introVideoLink field are set up correctly & whether they need to be added anywhere else
  private  get formOptions(): object {
    return {
      'topicIdentificationContent': [null],
      'title': [
        null,
        Validators.compose([
          Validators.required,
          Validators.maxLength(100)
        ])
      ],
      'description': [
        null,
        Validators.compose([
          Validators.required,
          Validators.maxLength(1000)
        ])
      ],
      'subject': [
        null,
        Validators.compose([
          Validators.required,
          Validators.maxLength(32)
        ])
      ],
      'introCopy': [null,
        Validators.compose([
          Validators.required,
        ])],
      'videoLink': [null,
        Validators.compose([
          Validators.required,
          Validators.pattern('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?')
        ])],
      'showOpenAnswerEnabled': [false],
      'openAnswerPrompt': [null,
        Validators.compose([
          Validators.required
        ])],
      'completeCopy': [null,
        Validators.compose([
          Validators.required,
        ])],
      'pacingGoal': [
        null,
        Validators.compose([
          Validators.required,
        ])
      ],
      'accuracyGoal': [
        null,
        Validators.compose([
          Validators.required,
        ])
      ],
    };
  }

}
